import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';

import Icon from '../../Img/Icon';
import { showPopupNew } from '../../../actions/popup-actions';
import { fetchAuthenticatedUser } from '../../../actions';
import { PopupTypes } from '../../../utils/constants';
import { PowerFunnelSurfaces, PowerFunnelGatedFeatures } from '../../Analytics/constants';

import styles from './PlayerBookmark.scss';

// Row surfaces (leaderboard / rows / search / event lists) use the Figma 10×16 skinny thin-outline
// bookmark, painted blue via `text-v3-blue` (TMS-2332 A4b). The profile-header banner uses its own
// heavier 16×25 glyph, painted white (A4a) — a distinct asset, not the row glyph.
const BOOKMARK_ROW_OUTLINE_ICON = '/img/v3/icons/icon-bookmark.svg';
const BOOKMARK_ROW_FILLED_ICON = '/img/v3/icons/icon-bookmark-on.svg';
const BOOKMARK_HEADER_OUTLINE_ICON = '/img/v3/icons/icon-bookmark-header.svg';
const BOOKMARK_HEADER_FILLED_ICON = '/img/v3/icons/icon-bookmark-header-on.svg';
export const ICON_SIZE = 24;
// `size` is the glyph HEIGHT; the width follows the viewBox aspect so the box matches the design
// instead of a padded square. Row glyph is 10×16, the header glyph 16×25.
const BOOKMARK_ROW_ASPECT_RATIO = 10 / 16;
const BOOKMARK_HEADER_ASPECT_RATIO = 16 / 25;
const BOOKMARK_VARIANT_HEADER = 'header';
// Row surfaces (global search, local rankings, event player lists) render the bookmark at the A4b
// 16px design height (10×16 glyph); the profile header keeps the larger ICON_SIZE.
export const BOOKMARK_ROW_ICON_HEIGHT = 16;
// Kept comfortably under UpgradeToPowerPopupV4's 5s ON_SUCCESS_TIMEOUT_MS window: that popup races
// onSuccess against its own 5s timeout without cancelling the loser, so an unbounded auth refresh
// could both swallow the payment confirmation and pop a late, contextless Save modal.
export const AUTH_REFRESH_TIMEOUT_MS = 3500;

const BookmarkStates = {
    FREE: 'free',
    LOADING: 'loading',
    OUTLINE: 'outline',
    FILLED: 'filled',
};

// Free users can't save players; the bookmark opens the Power upsell instead. Surface that on hover so
// the greyed glyph isn't a dead control. Component-level so every bookmark surface (rows, search, event
// lists, profile header) gets the same hint.
const FREE_USER_TOOLTIP = 'Upgrade to Power to save player lists';

const PlayerBookmark = (props) => {
    const {
        playerId,
        playerName,
        isSaved,
        isLoading,
        savedListIds,
        onSaveSuccess,
        onRemoveSuccess,
        className,
        size,
        variant,
        'data-testid': dataTestId,
    } = props;

    const isHeaderVariant = variant === BOOKMARK_VARIANT_HEADER;
    const aspectRatio = isHeaderVariant ? BOOKMARK_HEADER_ASPECT_RATIO : BOOKMARK_ROW_ASPECT_RATIO;

    const dispatch = useDispatch();
    const user = useSelector(({ auth }) => auth?.user);
    const isMountedRef = useRef(true);
    useEffect(() => () => { isMountedRef.current = false; }, []);

    if (!user) {
        return null;
    }

    const isPowered = !!user.savedListPowered;

    const showSaveToListPopup = () => {
        dispatch(showPopupNew({
            popup: PopupTypes.SAVE_TO_LIST_POPUP,
            id: playerId,
            playerName,
            savedListIds,
            onSuccess: onSaveSuccess,
        }));
    };

    const showRemoveConfirmPopup = () => {
        dispatch(showPopupNew({
            popup: PopupTypes.REMOVE_PLAYER_CONFIRM,
            id: playerId,
            playerName,
            onSuccess: onRemoveSuccess,
        }));
    };

    const resolveBookmarkState = () => {
        if (!isPowered) {
            return BookmarkStates.FREE;
        }
        if (isLoading) {
            return BookmarkStates.LOADING;
        }
        return isSaved ? BookmarkStates.FILLED : BookmarkStates.OUTLINE;
    };

    const bookmarkState = resolveBookmarkState();
    const outlineIcon = isHeaderVariant ? BOOKMARK_HEADER_OUTLINE_ICON : BOOKMARK_ROW_OUTLINE_ICON;
    const filledIcon = isHeaderVariant ? BOOKMARK_HEADER_FILLED_ICON : BOOKMARK_ROW_FILLED_ICON;
    const iconSrc = bookmarkState === BookmarkStates.FILLED ? filledIcon : outlineIcon;

    const openSaveToListModal = async () => {
        // The checkout wrote a possibly-partial currentMember into the store-wide auth.user, so the
        // refresh must run even after an unmount (the header/nav read the same auth.user). It's
        // bounded by its own timeout so a slow auth call can't outlast the popup's 5s window.
        let refreshOk = false;
        try {
            await Promise.race([
                dispatch(fetchAuthenticatedUser()), // NOSONAR — Redux thunk returns a promise
                new Promise((_, reject) => { setTimeout(() => reject(new Error('auth refresh timed out')), AUTH_REFRESH_TIMEOUT_MS); }),
            ]);
            refreshOk = true;
        } catch {
            refreshOk = false;
        }
        if (isMountedRef.current && refreshOk) {
            showSaveToListPopup();
            return;
        }
        // Navigated away, or the refresh failed / timed out: surface the standard Power success popup
        // (the upsell suppresses its own while an onSuccess is set) so a member who paid always gets a
        // confirmation rather than a silent close or a late, contextless modal.
        dispatch(showPopupNew({ popup: PopupTypes.POWER_SUCCESS_POPUP_V2 }));
    };

    // The bookmark renders inside a row's profile <Link>: preventDefault stops the native <a> and
    // stopPropagation stops react-router's synthetic nav, so opening a modal never also navigates.
    const suppressAncestorNavigation = (e) => {
        if (e) {
            e.stopPropagation();
            e.preventDefault();
        }
    };

    const handleClick = (e) => {
        suppressAncestorNavigation(e);
        if (bookmarkState === BookmarkStates.LOADING) {
            return;
        }
        if (!isPowered) {
            dispatch(showPopupNew({
                popup: PopupTypes.UPGRADE_TO_POWER_POPUP,
                referringSurface: PowerFunnelSurfaces.PLAYER_PROFILE,
                gatedFeature: PowerFunnelGatedFeatures.SAVED_PLAYER_LIST,
                onSuccess: openSaveToListModal,
            }));
            return;
        }
        if (isSaved) {
            // Adding an already-saved player to a further list (the multi-list picker) is a later
            // story, so a filled bookmark has no path back into SaveToListModal yet.
            showRemoveConfirmPopup();
            return;
        }
        showSaveToListPopup();
    };

    const buttonClass = [
        styles.bookmark,
        isHeaderVariant ? '' : styles.rowTarget,
        bookmarkState === BookmarkStates.FREE ? styles.greyed : '',
        bookmarkState === BookmarkStates.LOADING ? styles.loading : '',
        className,
    ].filter(Boolean).join(' ');

    return (
        <button
            type="button"
            className={buttonClass}
            onClick={handleClick}
            title={isPowered ? undefined : FREE_USER_TOOLTIP}
            aria-label={isSaved ? 'Remove from saved list' : 'Save to list'}
            aria-disabled={bookmarkState === BookmarkStates.LOADING || undefined}
            data-testid={dataTestId || undefined}
        >
            <span
                className={styles.iconWrapper}
                data-testid={dataTestId && `${dataTestId}.${bookmarkState}`}
            >
                <Icon src={iconSrc} height={size} width={size * aspectRatio} />
            </span>
        </button>
    );
};

PlayerBookmark.propTypes = {
    playerId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    playerName: PropTypes.string,
    isSaved: PropTypes.bool,
    isLoading: PropTypes.bool,
    savedListIds: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
    onSaveSuccess: PropTypes.func,
    onRemoveSuccess: PropTypes.func,
    className: PropTypes.string,
    // Bookmark HEIGHT in px (the width follows the glyph aspect). Defaults to the 24px profile
    // bookmark; row surfaces pass BOOKMARK_ROW_ICON_HEIGHT to match the 16px design height.
    size: PropTypes.number,
    // 'header' renders the 16×25 profile-header glyph (A4a); the default renders the 10×16 row glyph (A4b).
    variant: PropTypes.oneOf(['row', 'header']),
    'data-testid': PropTypes.string,
};

PlayerBookmark.defaultProps = {
    playerName: null,
    isSaved: false,
    isLoading: false,
    savedListIds: null,
    onSaveSuccess: null,
    onRemoveSuccess: null,
    className: null,
    size: ICON_SIZE,
    variant: 'row',
    'data-testid': null,
};

export default PlayerBookmark;
